import {
  signIn,
  requestPasswordRecovery,
  validateResetToken,
  completePasswordReset,
} from '../lib/auth.js';
import { loginHref, parseRoute, recuperarHref, AUTH_SCREENS, isPlainClick } from '../lib/router.js';
import { HERO_ICONS } from '../data/icons.js';

let onEnter = null;

const TITLES = {
  login: 'Entrar — Torre de Controle',
  recuperar: 'Recuperar senha — Torre de Controle',
  'set-password': 'Redefinir senha — Torre de Controle',
};

function authPathFor(screen) {
  if (screen === 'recuperar') return recuperarHref();
  if (screen === 'set-password') return `${loginHref().replace(/\/login$/, '/set-password')}${window.location.search || ''}`;
  return loginHref(new URLSearchParams(window.location.search).get('next'));
}

export function showLoginScreen() {
  const parsed = parseRoute();
  const screen = AUTH_SCREENS.has(parsed.screen) ? parsed.screen : 'login';
  if (parsed.screen !== screen) {
    const next = `${window.location.pathname}${window.location.search}`;
    history.replaceState({ screen: 'login' }, '', loginHref(next));
  }
  document.documentElement.setAttribute('data-screen', 'login');
  document.title = TITLES[screen] || TITLES.login;
  setAuthView(screen === 'set-password' ? 'reset' : screen === 'recuperar' ? 'forgot' : 'login');
  requestAnimationFrame(() => {
    if (screen === 'recuperar') document.getElementById('forgot-email')?.focus();
    else if (screen === 'set-password') document.getElementById('reset-pass')?.focus();
    else document.getElementById('login-email')?.focus();
  });
}

export function hideLoginScreen() {
  document.documentElement.removeAttribute('data-screen');
}

export function showBootScreen(title, sub) {
  const titleEl = document.querySelector('#boot-screen .boot-title');
  const subEl = document.querySelector('#boot-screen .boot-sub');
  if (titleEl && title) titleEl.textContent = title;
  if (subEl && sub) subEl.innerHTML = `${sub}<span class="boot-dots"></span>`;
  document.documentElement.setAttribute('data-screen', 'boot');
  document.title = 'Torre de Controle';
}

export function consumeLoginNext() {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next || AUTH_SCREENS.has(next.replace(/^\//, '').split(/[/?#]/)[0]) || next.startsWith('/login')) return null;
  return next;
}

export function setAuthView(view) {
  const views = ['login', 'forgot', 'forgot-sent', 'reset'];
  for (const name of views) {
    const el = document.getElementById(`auth-view-${name}`);
    if (el) el.hidden = name !== view;
  }
  const card = document.querySelector('.login-card');
  if (card) card.dataset.authView = view;
}

function showFieldError(id, msg) {
  const err = document.getElementById(id);
  if (!err) return;
  err.hidden = false;
  err.textContent = msg;
}

function hideFieldError(id) {
  const err = document.getElementById(id);
  if (!err) return;
  err.hidden = true;
  err.textContent = '';
}

function bindEye(button, input) {
  button?.addEventListener('click', () => {
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    button.innerHTML = show ? HERO_ICONS.eyeOff : HERO_ICONS.eye;
    button.setAttribute('aria-label', show ? 'Ocultar senha' : 'Mostrar senha');
    input.focus();
  });
}

function shake(form) {
  if (!form) return;
  form.classList.remove('login-shake');
  void form.offsetWidth;
  form.classList.add('login-shake');
}

async function loadResetToken() {
  const token = parseRoute().token || new URLSearchParams(window.location.search).get('token') || '';
  const hint = document.getElementById('reset-hint');
  const form = document.getElementById('reset-form');
  hideFieldError('reset-error');
  if (!token) {
    showFieldError('reset-error', 'Link de recuperação inválido.');
    if (form) form.querySelectorAll('input, button[type="submit"]').forEach((el) => { el.disabled = true; });
    if (hint) hint.textContent = '';
    return '';
  }
  if (form) form.querySelectorAll('input, button[type="submit"]').forEach((el) => { el.disabled = false; });
  if (hint) hint.textContent = 'Validando link…';
  try {
    const data = await validateResetToken(token);
    const invite = data?.data || {};
    const recovery = invite.purpose === 'password_recovery';
    const title = document.getElementById('reset-title');
    const lead = document.getElementById('reset-lead');
    const tag = document.getElementById('reset-tag');
    if (title) title.textContent = recovery ? 'Redefinir senha' : 'Definir senha';
    if (lead) lead.textContent = recovery
      ? 'Crie uma nova senha para acessar a torre.'
      : 'Conclua seu acesso definindo uma senha.';
    if (tag) tag.textContent = recovery ? 'recuperação de senha' : 'convite de acesso';
    if (hint) {
      hint.innerHTML = `Olá, <strong>${invite.name || invite.email || 'operador'}</strong>. ${
        recovery ? 'Defina uma nova senha para sua conta.' : 'Crie sua senha para ativar a conta.'
      }`;
    }
    return token;
  } catch (err) {
    showFieldError('reset-error', err.message || 'Convite expirado ou inválido.');
    if (hint) hint.textContent = '';
    if (form) form.querySelectorAll('input, button[type="submit"]').forEach((el) => { el.disabled = true; });
    return '';
  }
}

export function bindLogin({ onSuccess } = {}) {
  onEnter = onSuccess;
  const loginForm = document.getElementById('login-form');
  const forgotForm = document.getElementById('forgot-form');
  const resetForm = document.getElementById('reset-form');

  bindEye(document.getElementById('login-eye'), document.getElementById('login-pass'));
  bindEye(document.getElementById('reset-eye'), document.getElementById('reset-pass'));
  bindEye(document.getElementById('reset-eye-2'), document.getElementById('reset-pass-2'));

  document.getElementById('login-screen')?.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a || !isPlainClick(e)) return;
    let url;
    try { url = new URL(a.getAttribute('href'), window.location.origin); } catch { return; }
    if (url.origin !== window.location.origin) return;
    const parsed = parseRoute(url);
    if (!AUTH_SCREENS.has(parsed.screen)) return;
    e.preventDefault();
    history.pushState({ screen: parsed.screen }, '', `${url.pathname}${url.search}`);
    showLoginScreen();
    if (parsed.screen === 'set-password') loadResetToken();
  });

  loginForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideFieldError('login-error');
    const email = document.getElementById('login-email').value;
    const password = document.getElementById('login-pass').value;
    const remember = document.getElementById('login-remember').checked;
    const submit = document.getElementById('login-submit');
    if (!String(email).trim() || !password) {
      showFieldError('login-error', 'Preencha e-mail e senha.');
      return;
    }
    submit.disabled = true;
    submit.dataset.label = submit.textContent;
    submit.textContent = 'Entrando…';
    try {
      const session = await signIn(email, password, remember);
      await onEnter?.(session);
    } catch (err) {
      showFieldError('login-error', err.message || 'E-mail ou senha inválidos.');
      shake(loginForm);
    } finally {
      submit.disabled = false;
      submit.textContent = submit.dataset.label || 'Entrar na torre';
    }
  });

  forgotForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideFieldError('forgot-error');
    const email = document.getElementById('forgot-email').value;
    const submit = document.getElementById('forgot-submit');
    if (!String(email).trim() || !String(email).includes('@')) {
      showFieldError('forgot-error', 'Informe um e-mail válido.');
      return;
    }
    submit.disabled = true;
    submit.dataset.label = submit.textContent;
    submit.textContent = 'Enviando…';
    try {
      await requestPasswordRecovery(email);
      setAuthView('forgot-sent');
    } catch (err) {
      showFieldError('forgot-error', err.message || 'Não foi possível enviar o link.');
      shake(forgotForm);
    } finally {
      submit.disabled = false;
      submit.textContent = submit.dataset.label || 'Enviar link de recuperação';
    }
  });

  resetForm?.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideFieldError('reset-error');
    const token = parseRoute().token || new URLSearchParams(window.location.search).get('token') || '';
    const password = document.getElementById('reset-pass').value;
    const passwordConfirmation = document.getElementById('reset-pass-2').value;
    const submit = document.getElementById('reset-submit');
    if (password.length < 8) {
      showFieldError('reset-error', 'A senha deve ter ao menos 8 caracteres.');
      return;
    }
    if (password !== passwordConfirmation) {
      showFieldError('reset-error', 'As senhas não coincidem.');
      return;
    }
    submit.disabled = true;
    submit.dataset.label = submit.textContent;
    submit.textContent = 'Salvando…';
    try {
      await completePasswordReset({ token, password, passwordConfirmation });
      const done = document.getElementById('reset-done');
      if (done) {
        done.hidden = false;
        done.textContent = 'Senha redefinida com sucesso. Redirecionando para o login…';
      }
      resetForm.querySelectorAll('input, button[type="submit"]').forEach((el) => { el.disabled = true; });
      window.setTimeout(() => {
        history.replaceState({ screen: 'login' }, '', loginHref());
        showLoginScreen();
      }, 1600);
    } catch (err) {
      showFieldError('reset-error', err.message || 'Não foi possível redefinir a senha.');
      shake(resetForm);
    } finally {
      submit.disabled = false;
      submit.textContent = submit.dataset.label || 'Salvar nova senha';
    }
  });

  if (parseRoute().screen === 'set-password') {
    loadResetToken();
  }
}

export { loadResetToken, authPathFor };
